import { InfoCircleFilled } from '@ant-design/icons';
import { Col, Modal, Row, Steps, Typography } from 'antd';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useOrganization } from '../../api/Onboarding';
import { useCreateTranscript } from '../../api/Transcripts';
import Loading from '../../components/Loading';
import { SurveyProcessingType } from '../../constants';
import { BluePrimary } from '../../styles';
import CodingGuidance from './CodingGuidance';
import FileUploadForm from './FileUploadForm';
import UploadFailed from './UploadFailed';
import UploadSuccessful from './UploadSuccessful';

const { error } = Modal;
const { Paragraph } = Typography;

const StyledRow = styled(Row)`
  width: 100%;
  padding: 32px 0;
`;

const StyledInfoCircleFilled = styled(InfoCircleFilled)`
  color: ${BluePrimary} !important;
`;

function UploadTranscript() {
  const navigate = useNavigate();

  const [current, setCurrent] = useState(0);
  const [transcriptData, setTranscriptData] = useState({});
  const [codingGuidanceSelected, setCodingGuidanceSelected] = useState(false);

  const createTranscript = useCreateTranscript();
  const { data: organization, isLoading: organizationLoading } = useOrganization({
    onSuccess: (data) => {
      if (data.data.available_tokens === 0) {
        error({
          title: 'No tokens, no problem!',
          content: (
            <>
              <Paragraph>You&apos;re out of tokens, but we can fix that fast.</Paragraph>
              <Paragraph>
                Contact <a href="mailto:support@fathomthat.ai">support@fathomthat.ai</a> to add
                tokens and get your data processing right away!
              </Paragraph>
            </>
          ),
          icon: null,
          onOk() {
            navigate('/');
          },
        });
      }
    },
  });

  const next = () => {
    setCurrent(current + 1);
  };

  const prev = () => {
    setCurrent(current - 1);
  };

  const retryUpload = () => {
    createTranscript.reset();
    prev();
  };

  const handleUploadTranscript = (transcriptDataOverride = null) => {
    const effectiveTranscriptData = transcriptDataOverride || transcriptData;
    const details = effectiveTranscriptData?.transcriptDetails;

    if (!details?.interview_guide_gdrive_id || !details?.transcript_gdrive_ids?.length) {
      error({
        title: 'Missing upload data',
        content:
          'Your interview guide and/or transcript files were not fully prepared. Please go back and try uploading again.',
      });
      return;
    }

    // Prepare data as JSON (files already uploaded to Google Drive)
    const data = {
      interview_guide_gdrive_id: details.interview_guide_gdrive_id,
      transcript_gdrive_ids: details.transcript_gdrive_ids,
      project_name: details.project_name,
      research_goals: details.research_goals,
      processing_type: SurveyProcessingType.PRO,
      total_oe_responses: details.total_oe_responses,
    };

    // Add coding guidance questions with their processing metadata
    if (effectiveTranscriptData.questions) {
      const questionsMetadata = {};
      effectiveTranscriptData.questions.forEach((question, index) => {
        questionsMetadata[index] = {
          text: question.text,
          ...(question.processing_type ? { processing_type: question.processing_type } : {}),
          ...(question.import_code_frame_id
            ? { import_code_frame_id: question.import_code_frame_id }
            : {}),
        };
      });
      data.questions_metadata = JSON.stringify(questionsMetadata);
    }

    createTranscript.mutate({ data: JSON.stringify(data) });
  };

  const finishUpload = (transcriptDataOverride = null) => {
    const effectiveTranscriptData = transcriptDataOverride || transcriptData;
    const numQuestions = effectiveTranscriptData.questions?.length || 0;
    const numTranscripts =
      effectiveTranscriptData.transcriptDetails?.transcript_gdrive_ids?.length || 0;
    const totalOEResponses = effectiveTranscriptData.transcriptDetails?.total_oe_responses || 0;
    const availableTokens = organization?.data.available_tokens;
    const transcriptMultiplier = organization?.data.transcript_multiplier || 5;

    // Calculate tokens based on account type and processing type
    const tokensToDeduct = totalOEResponses * transcriptMultiplier;

    Modal.confirm({
      title: `This project will use ${tokensToDeduct} tokens`,
      content: (
        <>
          <Paragraph>
            <i>{`${totalOEResponses} responses processed at ${transcriptMultiplier} tokens each`}</i>
          </Paragraph>
          <Paragraph>
            You have {availableTokens} tokens now, and will have {availableTokens - tokensToDeduct}{' '}
            after uploading.
          </Paragraph>
          <Paragraph>
            <Typography.Text strong>Project Details:</Typography.Text>
          </Paragraph>
          <Paragraph>
            • {numQuestions} interview questions
            <br />• {numTranscripts} transcript files
            <br />• {totalOEResponses} interview responses
          </Paragraph>
        </>
      ),
      icon: <StyledInfoCircleFilled />,
      okText: "Let's go!",
      onOk() {
        handleUploadTranscript(effectiveTranscriptData);
      },
      width: '50%',
    });
  };

  if (organizationLoading) {
    return <Loading />;
  }

  if (createTranscript.isSuccess) {
    return <UploadSuccessful />;
  }

  if (createTranscript.isError) {
    return <UploadFailed retryUpload={retryUpload} />;
  }

  const steps = [
    {
      title: 'Upload interviews',
      content: (
        <FileUploadForm
          setTranscriptData={setTranscriptData}
          transcriptData={transcriptData}
          next={next}
          setCodingGuidanceSelected={setCodingGuidanceSelected}
          finishUpload={finishUpload}
        />
      ),
    },
  ];

  if (codingGuidanceSelected) {
    steps.push({
      title: 'Select method',
      content: (
        <CodingGuidance
          transcriptData={transcriptData}
          prev={prev}
          createTranscriptLoading={createTranscript.isLoading}
          finishUpload={finishUpload}
          setTranscriptData={setTranscriptData}
        />
      ),
    });
  }

  return (
    <StyledRow align="center">
      <Col span={18}>
        {current > 0 && steps[current]?.title !== 'Upload interviews' && (
          <Steps progressDot current={current} items={steps} />
        )}
      </Col>
      <Col span={18}>{steps[current]?.content}</Col>
    </StyledRow>
  );
}

export default UploadTranscript;
